import { Metric, StateBlock } from "@/components/layout-2/ds";
import { InlineAlert } from "@/components/layout-2/ds-settings";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { formatNumber } from "@/utils/format-fr";
import { useEffect, useRef } from "react";
import { feedingClient } from "./api";
import type { DraftChange } from "./draft";
import type { Impact } from "./types";
import { useLoad } from "./use-load";

const CHANGE_LABEL = { added: "Added", removed: "Removed", changed: "Changed" } as const;

/**
 * Second step of a step dialog on a campaign in service: what saving this step would change. The server
 * computes it from the very body the Save sends (nothing is written); the caller's Save stays disabled
 * until `impact` arrived and the box is ticked: there is no way to save "blind".
 */
export function ImpactReview({
    campaignCode,
    body,
    changes,
    acknowledged,
    onAcknowledgedChange,
    pause,
    onPauseChange,
    onImpact
}: {
    campaignCode: string;
    body: Record<string, unknown>;
    changes: DraftChange[];
    acknowledged: boolean;
    onAcknowledgedChange: (value: boolean) => void;
    /** Pause the campaign with the Save; offered (and ticked) when the server suggests it. */
    pause: boolean;
    onPauseChange: (value: boolean) => void;
    /** The computed impact, or null while it loads or failed. */
    onImpact: (impact: Impact | null) => void;
}) {
    // What is measured is the body of the step when the review opened.
    const measured = useRef(body);
    const { data: impact, error, loading, reload } = useLoad<Impact>(() => feedingClient(campaignCode, "impact/").post(measured.current).json<Impact>(), [campaignCode], "The impact could not be computed.");
    useEffect(() => {
        onImpact(impact);
        onAcknowledgedChange(false);
        onPauseChange(!!impact?.suggest_pause);
    }, [impact]); // eslint-disable-line react-hooks/exhaustive-deps

    // A number the server did not compute (source over the ceiling) is a dash, not a zero.
    const count = (value: number | null, prefix = "") => (value === null ? "—" : `${prefix}${formatNumber(value)}`);
    // Only the numbers this change moves: a zero says nothing here.
    const metrics = impact
        ? [
              { label: "Matching now", value: count(impact.before), show: true },
              { label: "Would match", value: count(impact.after), show: impact.after !== impact.before },
              { label: "Entering", value: count(impact.entering, "+"), show: impact.entering !== 0 },
              { label: "Leaving", value: count(impact.leaving, "−"), hint: "They keep their workflow", show: impact.leaving !== 0 },
              { label: "No longer blocked", value: count(impact.unblocked, "+"), hint: "Freed by a removed exclusion", show: impact.unblocked !== 0 },
              { label: "Cards ended", value: count(impact.excluded_cards, "−"), hint: "In progress, caught by a new exclusion", show: impact.excluded_cards !== 0 }
          ].filter(metric => metric.show)
        : [];

    return (
        <div className="flex flex-col gap-4" data-testid="impact-review">
            <div className="flex flex-col gap-1 text-sm">
                <span className="text-xs font-medium text-gray-600">You are changing</span>
                <ul className="flex flex-col gap-1" data-testid="impact-changes">
                    {changes.map((change, index) => (
                        <li key={index} className="flex flex-wrap items-center gap-2">
                            <Badge tone="neutral">{CHANGE_LABEL[change.kind]}</Badge>
                            <span className="text-gray-600">{change.label}</span>
                            {change.before && <span className="text-gray-900 line-through">{change.before}</span>}
                            {change.after && <span className="font-semibold text-gray-900">{change.after}</span>}
                        </li>
                    ))}
                </ul>
            </div>

            {loading && <StateBlock kind="loading" message={<span data-testid="impact-loading">Computing the impact…</span>} className="py-10" />}

            {error && (
                <InlineAlert
                    type="error"
                    title="The impact could not be computed"
                    action={
                        <Button variant="outline" size="sm" onClick={reload}>
                            Retry
                        </Button>
                    }
                >
                    <span data-testid="impact-error">{error} Saving is disabled: an unmeasured change is not allowed.</span>
                </InlineAlert>
            )}

            {impact && (
                <div className="flex flex-col gap-3" data-testid="impact-ready">
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                        {metrics.map(metric => (
                            <Metric key={metric.label} label={metric.label} value={metric.value} hint={metric.hint} />
                        ))}
                    </div>
                    {!impact.too_large && (
                        <p className="text-sm text-gray-900">
                            <span className="font-semibold">At the next pass</span> — about {count(impact.would_inject)} of the entering records and {count(impact.unblocked_would_inject)} of the freed ones could get a card (cap{" "}
                            {formatNumber(impact.next_pass.cap)}, {impact.next_pass.order_label}), and {count(impact.would_block)} entering records would be blocked by an exclusion.
                        </p>
                    )}
                    {impact.alerts.map((alert, index) => (
                        <InlineAlert key={index} type={alert.type} title={alert.title}>
                            {alert.text}
                        </InlineAlert>
                    ))}
                    {impact.suggest_pause && (
                        <label className="flex items-start gap-2 rounded-sm border bg-gray-50 p-3 text-sm" data-testid="pause-with-save">
                            <Checkbox checked={pause} onCheckedChange={value => onPauseChange(value === true)} aria-label="Pause the campaign when saving" />
                            <span className="flex flex-col gap-0.5">
                                <span className="font-medium text-black">Pause the campaign when saving</span>
                                <span className="text-gray-600">Nothing is injected nor called until you resume it: set the filters first, then resume the campaign from the Feeding tab.</span>
                            </span>
                        </label>
                    )}
                    <label className="flex items-start gap-2 text-sm">
                        <Checkbox checked={acknowledged} onCheckedChange={value => onAcknowledgedChange(value === true)} aria-label="I have read the impact" />
                        <span>{impact.risky ? "I understand this change is risky and want to save it anyway." : "I have read the impact and want to save this change."}</span>
                    </label>
                </div>
            )}
        </div>
    );
}
